import { ApiError, toApiError } from './errors'
import type { AuthTokens, TokenStore } from './tokens'
import { parseTokens } from './tokens'

export type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Send the bearer token. Default true; set false for /auth/* calls. */
  auth?: boolean
  query?: Record<string, QueryValue>
}

export interface ApiClientOptions {
  /** e.g. http://localhost:8080/api/v1 */
  baseUrl: string
  tokenStore: TokenStore
  /** '/auth/refresh' for the client app, '/admin/auth/refresh' for the admin panel. */
  refreshPath?: string
  fetchImpl?: typeof fetch
  /** Refresh failed — the session is gone, send the user to the login screen. */
  onAuthLost?: () => void
  /** Every failed request, for toasts and Faro logging (`api.request.failed`). */
  onError?: (error: ApiError) => void
  /**
   * Current UI language, sent as `X-Lang` on every request: the backend then
   * answers names, texts and messages in it (falling back to Russian).
   */
  language?: () => string
}

export interface ApiClient {
  request<T>(path: string, options?: RequestOptions): Promise<T>
  get<T>(path: string, options?: RequestOptions): Promise<T>
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  del<T>(path: string, options?: RequestOptions): Promise<T>
  /**
   * A file the server builds (CSV exports): the same auth and one refresh on
   * 401 as every request, but the body comes back as a Blob, not JSON.
   */
  download(path: string, options?: RequestOptions): Promise<Blob>
  refresh(): Promise<AuthTokens>
}

export function buildUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, QueryValue>,
): string {
  const url = `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

/**
 * Bodies the browser must send as they are: a file upload needs its own
 * multipart boundary, and JSON.stringify would turn it into "[object FormData]".
 */
export function isRawBody(body: unknown): body is BodyInit {
  return (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body)
  )
}

async function parseBody<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

/**
 * One client per app. Refreshes at most once per 401 and allows only one refresh
 * at a time — parallel requests queue behind it, or the rotating refresh token
 * trips the backend's reuse protection (docs/frontend/README.md §Tokens).
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const {
    baseUrl,
    tokenStore,
    refreshPath = '/auth/refresh',
    onAuthLost,
    onError,
    language,
  } = options
  const doFetch: typeof fetch = options.fetchImpl ?? ((...args) => globalThis.fetch(...args))
  let refreshing: Promise<AuthTokens> | null = null

  async function send(path: string, opts: RequestOptions, withAuth: boolean): Promise<Response> {
    const { body, query, headers, ...init } = opts
    const finalHeaders = new Headers(headers)
    const raw = isRawBody(body)
    if (!finalHeaders.has('Accept')) finalHeaders.set('Accept', 'application/json')
    if (language) finalHeaders.set('X-Lang', language())
    // A raw body carries its own type — setting it here would drop the
    // multipart boundary the browser generates.
    if (body !== undefined && !raw && !finalHeaders.has('Content-Type')) {
      finalHeaders.set('Content-Type', 'application/json')
    }
    if (withAuth) {
      const tokens = tokenStore.get()
      if (tokens) finalHeaders.set('Authorization', `Bearer ${tokens.accessToken}`)
    }
    return doFetch(buildUrl(baseUrl, path, query), {
      ...init,
      headers: finalHeaders,
      body: body === undefined ? undefined : raw ? body : JSON.stringify(body),
    })
  }

  async function runRefresh(): Promise<AuthTokens> {
    const current = tokenStore.get()
    if (!current) {
      throw new ApiError({ code: 'unauthorized', status: 401, message: 'no refresh token' })
    }
    const response = await send(
      refreshPath,
      { method: 'POST', body: { refresh_token: current.refreshToken } },
      false,
    )
    if (!response.ok) {
      tokenStore.set(null)
      onAuthLost?.()
      throw await toApiError(response)
    }
    const tokens = parseTokens(await parseBody(response))
    tokenStore.set(tokens)
    return tokens
  }

  function refresh(): Promise<AuthTokens> {
    refreshing ??= runRefresh().finally(() => {
      refreshing = null
    })
    return refreshing
  }

  /** Sends, refreshes once on 401 and turns a failed answer into an ApiError. */
  async function sendOk(path: string, opts: RequestOptions): Promise<Response> {
    const withAuth = opts.auth !== false
    let response = await send(path, opts, withAuth)

    if (response.status === 401 && withAuth && tokenStore.get()) {
      await refresh()
      response = await send(path, opts, true)
    }

    if (!response.ok) {
      const error = await toApiError(response)
      onError?.(error)
      throw error
    }
    return response
  }

  async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return parseBody<T>(await sendOk(path, opts))
  }

  return {
    request,
    get<T>(path: string, opts?: RequestOptions) {
      return request<T>(path, { ...opts, method: 'GET' })
    },
    post<T>(path: string, body?: unknown, opts?: RequestOptions) {
      return request<T>(path, { ...opts, method: 'POST', body })
    },
    put<T>(path: string, body?: unknown, opts?: RequestOptions) {
      return request<T>(path, { ...opts, method: 'PUT', body })
    },
    patch<T>(path: string, body?: unknown, opts?: RequestOptions) {
      return request<T>(path, { ...opts, method: 'PATCH', body })
    },
    del<T>(path: string, opts?: RequestOptions) {
      return request<T>(path, { ...opts, method: 'DELETE' })
    },
    async download(path: string, opts?: RequestOptions) {
      const headers = new Headers(opts?.headers)
      if (!headers.has('Accept')) headers.set('Accept', '*/*')
      const response = await sendOk(path, { ...opts, method: 'GET', headers })
      return response.blob()
    },
    refresh,
  }
}
